"use client";
import { parseCompletionUndoAction, type CompletionUndoAction } from "../completionUndoAction";
import { completionUndoFinalizationRequestSchema } from "../completionUndoFinalization";
import type { CompletionUndoRecoveryGuard } from "../completionDelivery";
import { WorkspaceOwnerChangedError } from "@/src/features/workspace/client/workspacePersistenceErrors";

/** Keep the captured source through uncertain acknowledgements; never look up a new undo head. */
export async function applyCompletionUndoAndFinalize<T>(value: unknown, options: {
  ownerId: string; companyId: string; isCurrent(): boolean;
  recovery?: CompletionUndoRecoveryGuard;
  alreadyUndone?: boolean;
  apply(action: CompletionUndoAction): Promise<T>;
}) {
  const current = () => { if (!options.isCurrent()) throw new WorkspaceOwnerChangedError(); };
  current();
  if (!value || typeof value !== "object" || !("status" in value) || value.status !== "pending" && value.status !== "completed") throw new Error("Invalid undo action status");
  const completed = value.status === "completed";
  if (options.alreadyUndone && !options.recovery) throw new Error("Recovered acknowledgement requires its original registration");
  const action = parseCompletionUndoAction({ ...value, status: "pending" }, options.ownerId, options.companyId);
  const request = completionUndoFinalizationRequestSchema.parse({ expectedOwnerId: options.ownerId, expectedCompanyId: options.companyId,
    requestReactId: action.undoRequest.reactId, previewId: action.undoRequest.previewId, componentId: action.componentId, source: action.undoTarget.undo!.source,
    ...(options.recovery ? { recovery: options.recovery } : {}) });
  const effect = completed || options.alreadyUndone ? null : await options.apply(action);
  current();
  const { requestCompletionUndoFinalization } = await import("./completionUndoFinalization");
  current();
  const finalization = await requestCompletionUndoFinalization(request, { isCurrent: options.isCurrent });
  current();
  return { effect, finalization };
}
